/**
 * PhaseGroupNode — Visual container for workflow phases.
 *
 * Renders as a labeled background band that groups nodes belonging
 * to the same config phase. Uses React Flow v12 group node pattern.
 */

import React, { memo } from 'react';
import type { Node, NodeProps } from '@xyflow/react';
import { Badge, Box, Group, Text } from '@mantine/core';
import { IconStack2 } from '@tabler/icons-react';

export interface PhaseGroupData extends Record<string, unknown> {
  label: string;
  phaseId: string;
  order: number;
  engine: string;
  description?: string;
}

/** React Flow v12 typed node for phase groups. */
export type PhaseGroupNodeType = Node<PhaseGroupData, 'phaseGroup'>;

const ENGINE_COLORS: Record<string, string> = {
  dag_workflow: 'blue',
  state_machine: 'violet',
  saga: 'pink',
  actor: 'teal',
  zen_engine: 'red',
};

function PhaseGroupNodeInner({ data }: NodeProps<PhaseGroupNodeType>) {
  const engineColor = ENGINE_COLORS[data.engine] ?? 'gray';

  return (
    <Box
      style={{
        width: '100%',
        height: '100%',
        background: 'var(--mantine-color-gray-0)',
        border: '1px dashed var(--mantine-color-gray-4)',
        borderRadius: 8,
        padding: 8,
        pointerEvents: 'none',
      }}
    >
      <Group gap={6} mb={4}>
        <IconStack2 size={14} color="var(--mantine-color-gray-6)" />
        <Text size="xs" fw={600} c="dimmed">
          Phase {data.order}: {data.label}
        </Text>
        <Badge size="xs" variant="light" color={engineColor}>
          {data.engine}
        </Badge>
      </Group>
    </Box>
  );
}

export const PhaseGroupNode = memo(PhaseGroupNodeInner);
